/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Data Subject Rights Types
 * Based on LGPD Art. 18, GDPR Arts. 15-22, and CCPA
 */

export type DataRightType =
  | 'confirmation' // Confirmation of processing
  | 'access' // Access to data
  | 'correction' // Data correction
  | 'anonymization' // Anonymization, blocking, or deletion
  | 'portability' // Data portability
  | 'deletion-consent' // Deletion of data processed with consent
  | 'sharing-info' // Information about data sharing
  | 'consent-info' // Information about possibility of not consenting
  | 'revocation' // Revocation of consent
  | 'opposition' // Opposition to processing
  | 'automated-review'; // Review of automated decisions

export interface DataRightRequest {
  id?: string;
  userId: string;
  userEmail: string;
  userName: string;
  requestType: DataRightType;
  description: string;
  status: 'pending' | 'in-progress' | 'completed' | 'rejected';
  createdAt: Date;
  updatedAt?: Date;
  completedAt?: Date;
  protocolNumber?: string;
  dpoNotes?: string;
}

export interface DataRightOption {
  type: DataRightType;
  titleKey: string;
  descriptionKey: string;
  icon: string;
  requiresAdditionalInfo: boolean;
}

export const DATA_RIGHTS_OPTIONS: DataRightOption[] = [
  {
    type: 'confirmation',
    titleKey: 'dataRights.rights.confirmation.title',
    descriptionKey: 'dataRights.rights.confirmation.description',
    icon: 'check-circle',
    requiresAdditionalInfo: false,
  },
  {
    type: 'access',
    titleKey: 'dataRights.rights.access.title',
    descriptionKey: 'dataRights.rights.access.description',
    icon: 'download',
    requiresAdditionalInfo: false,
  },
  {
    type: 'correction',
    titleKey: 'dataRights.rights.correction.title',
    descriptionKey: 'dataRights.rights.correction.description',
    icon: 'edit',
    requiresAdditionalInfo: true,
  },
  {
    type: 'anonymization',
    titleKey: 'dataRights.rights.anonymization.title',
    descriptionKey: 'dataRights.rights.anonymization.description',
    icon: 'trash',
    requiresAdditionalInfo: true,
  },
  {
    type: 'portability',
    titleKey: 'dataRights.rights.portability.title',
    descriptionKey: 'dataRights.rights.portability.description',
    icon: 'upload',
    requiresAdditionalInfo: false,
  },
  {
    type: 'deletion-consent',
    titleKey: 'dataRights.rights.deletionConsent.title',
    descriptionKey: 'dataRights.rights.deletionConsent.description',
    icon: 'x-circle',
    requiresAdditionalInfo: false,
  },
  {
    type: 'sharing-info',
    titleKey: 'dataRights.rights.sharingInfo.title',
    descriptionKey: 'dataRights.rights.sharingInfo.description',
    icon: 'share',
    requiresAdditionalInfo: false,
  },
  {
    type: 'consent-info',
    titleKey: 'dataRights.rights.consentInfo.title',
    descriptionKey: 'dataRights.rights.consentInfo.description',
    icon: 'info',
    requiresAdditionalInfo: false,
  },
  {
    type: 'revocation',
    titleKey: 'dataRights.rights.revocation.title',
    descriptionKey: 'dataRights.rights.revocation.description',
    icon: 'x',
    requiresAdditionalInfo: true,
  },
  {
    type: 'opposition',
    titleKey: 'dataRights.rights.opposition.title',
    descriptionKey: 'dataRights.rights.opposition.description',
    icon: 'alert-triangle',
    requiresAdditionalInfo: true,
  },
  {
    type: 'automated-review',
    titleKey: 'dataRights.rights.automatedReview.title',
    descriptionKey: 'dataRights.rights.automatedReview.description',
    icon: 'cpu',
    requiresAdditionalInfo: true,
  },
];
